import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Button } from '../button/button';
import { Card, CardContent, CardHeader, CardTitle } from '../card';
import { Input } from '../input';
import type { Agent } from '../../../lib/types';
import type { ManagedAgentImport } from '../../../lib/managed-agents/types';

interface ManagedAgentDetailsProps {
  agent: Agent;
  agentImport: ManagedAgentImport | undefined;
  onSaveGreeting: (greeting: string) => Promise<unknown>;
  onSaveBudget: (budgetCents: number) => Promise<unknown>;
  onClose: () => void;
}

/**
 * An imported agent. Its prompt and tools live in the customer's Anthropic
 * account; only the opening message belongs to PromptSafe.
 */
export function ManagedAgentDetails({
  agent,
  agentImport,
  onSaveGreeting,
  onSaveBudget,
  onClose,
}: ManagedAgentDetailsProps) {
  const savedGreeting = agent.greeting ?? '';
  const [greeting, setGreeting] = useState(savedGreeting);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => setGreeting(savedGreeting), [savedGreeting]);

  const saveGreeting = async () => {
    setIsSaving(true);
    try {
      await onSaveGreeting(greeting.trim());
      toast.success('Opening message saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const rows: [string, string][] = agentImport
    ? [
        ['Model', agent.model ?? '—'],
        ['Pinned version', `v${agentImport.anthropic_agent_version}`],
        ['Anthropic agent ID', agentImport.anthropic_agent_id],
        ['Environment ID', agentImport.environment_id],
        [
          'Tools that need approval',
          agentImport.tool_policy === 'allow' ? 'Allowed' : 'Refused',
        ],
        [
          'Last seen working',
          agentImport.last_verified_at
            ? format(
                new Date(agentImport.last_verified_at),
                'd MMM yyyy, HH:mm',
              )
            : 'Not tested yet',
        ],
      ]
    : [];

  return (
    <Card className="rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a]">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-prompt-safe-com-semantic-heading-5 text-prompt-safecomvulcan">
          {agent.name}
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label="Close"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <p className="text-sm text-prompt-safecomabbey">
          Imported from Claude Console. To change its prompt or tools, edit it
          there, then update it on the{' '}
          <Link
            to="/connections"
            className="font-medium text-prompt-safecomoutrageous-orange hover:underline"
          >
            Connections
          </Link>{' '}
          page.
        </p>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="managed-agent-greeting"
            className="text-sm font-medium text-prompt-safecomvulcan"
          >
            Opening message
          </label>
          <div className="flex gap-2">
            <Input
              id="managed-agent-greeting"
              value={greeting}
              maxLength={500}
              onChange={(event) => setGreeting(event.target.value)}
              placeholder="Optional: your agent's opening message"
              className="bg-prompt-safecomwhite"
            />
            <Button
              size="sm"
              className="h-9"
              disabled={isSaving || greeting.trim() === savedGreeting}
              onClick={saveGreeting}
            >
              Save
            </Button>
          </div>
          <p className="text-xs text-prompt-safecomabbey">
            Optional. If your product shows a first message, add it here. Your
            agent never sends it, but the persona replies as if it did. Leave it
            blank and the persona starts the conversation.
          </p>
        </div>

        {agentImport && (
          <SpendLimitField
            savedCents={agentImport.budget_cents}
            onSave={onSaveBudget}
          />
        )}

        {agentImport ? (
          <dl className="grid grid-cols-[auto,1fr] gap-x-6 gap-y-2 rounded-lg border border-[#00000014] bg-prompt-safecomwhite p-4 text-sm">
            {rows.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-prompt-safecomabbey">{label}</dt>
                <dd className="break-all font-mono text-xs leading-5 text-prompt-safecomvulcan">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm text-red-700">
            Import settings are missing. Remove this agent and import it again.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** The customer's own spend limit per simulation, enforced by Anthropic. */
function SpendLimitField({
  savedCents,
  onSave,
}: {
  savedCents: number;
  onSave: (budgetCents: number) => Promise<unknown>;
}) {
  const saved = (savedCents / 100).toFixed(2);
  const [dollars, setDollars] = useState(saved);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => setDollars(saved), [saved]);

  const cents = Math.round(Number(dollars) * 100);
  const isValid = Number.isFinite(cents) && cents >= 1 && cents <= 100_000;

  const save = async () => {
    setIsSaving(true);
    try {
      await onSave(cents);
      toast.success('Spend limit saved');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor="managed-agent-budget"
        className="text-sm font-medium text-prompt-safecomvulcan"
      >
        Spend limit per simulation
      </label>
      <div className="flex items-center gap-2">
        <span className="text-sm text-prompt-safecomabbey">$</span>
        <Input
          id="managed-agent-budget"
          type="number"
          min="0.01"
          max="1000"
          step="0.01"
          value={dollars}
          onChange={(event) => setDollars(event.target.value)}
          className="max-w-[140px] bg-prompt-safecomwhite tabular-nums"
        />
        <Button
          size="sm"
          className="h-9"
          disabled={!isValid || isSaving || cents === savedCents}
          onClick={save}
        >
          Save
        </Button>
      </div>
      <p className="text-xs text-prompt-safecomabbey">
        Your Anthropic account pays for the agent. Anthropic stops a simulation
        when it reaches this amount. Applies to new simulations.
      </p>
    </div>
  );
}
